/**
 * Dated scoring-methodology notices.
 *
 * When a scoring rule changes, a customer's number can move without anything
 * on their site changing. That is confusing at best and looks like a bug at
 * worst, so every such change ships WITH its explanation, attached to the
 * score itself rather than buried in a changelog nobody opens.
 *
 * This file is the editable source of that copy — edit the strings here, not
 * the component. `/platform/methodology` renders the same list as the
 * canonical archive, so the in-product note and the reference page can never
 * drift apart.
 */

export interface ScoringNotice {
  /** Stable id — also the anchor on the methodology page. */
  id: string
  /** ISO date the change went live. */
  date: string
  /** Human date as shown in the heading. */
  displayDate: string
  title: string
  /** Rendered as separate paragraphs, in order. */
  body: string[]
  /** Which tools' result surfaces should carry the notice. */
  tools: string[]
}

export const SCORING_NOTICES: ScoringNotice[] = [
  {
    id: 'schema-audit-v2-2026-08-03',
    date: '2026-08-03',
    displayDate: '3 August 2026',
    title: 'Schema Audit scoring methodology v2',
    body: [
      'Schema Audit now scores business identity, WebSite, navigation context, and a page type appropriate to the audited content. Article, FAQPage, and SpeakableSpecification are no longer universal requirements, and ProfessionalService, Place, and CreativeWork now have field-completeness rubrics.',
      'Volume now counts typed schema entities rather than type memberships described as JSON-LD blocks, and volume and evaluated-type diversity build one point at a time instead of maxing out at five. If your score changed without a site update, compare runs by scoring-method version; version 1 and version 2 are not directly comparable.',
    ],
    tools: ['schema-audit', 'platform-dashboard'],
  },
  {
    id: 'withheld-tools-excluded-2026-07-20',
    date: '2026-07-20',
    displayDate: '20 July 2026',
    title: 'Withheld checks no longer count as zero',
    body: [
      'When a check cannot grade your site — usually because a firewall blocked it — we say so instead of inventing a number. That "withheld" result was still being averaged into your overall score as a zero, which pulled the total down for a check that had explicitly declined to score you.',
      'Withheld checks are now left out of the average entirely. If your overall score went up, nothing on your site changed; the earlier number was unfairly low. We now also show how many checks the score is based on, so a score built on 12 checks is never mistaken for one built on 13.',
    ],
    // Aggregate-only: the free single-tool pages never averaged anything.
    tools: ['platform-dashboard'],
  },
  {
    id: 'soft-404-scoring-2026-07-20',
    date: '2026-07-20',
    displayDate: '20 July 2026',
    title: 'Scoring methodology updated',
    body: [
      'We fixed how the AI Crawl Check scores a site that returns a normal web page instead of a proper "404 not found" when a file (like llms.txt or robots.txt) is missing. We used to treat that as "couldn\'t check" and drop it from your score; it actually means the file simply isn\'t there, so it now counts as missing — the same as any other site without one.',
      'If your score went down, nothing on your site changed for the worse. The old number was too generous, and the new one matches what any site with the same setup would get. The way to raise it is what it always was: add the missing file.',
    ],
    tools: ['crawl-check', 'platform-dashboard'],
  },
]

/** The notices that apply to a given tool, newest first. */
export function noticesForTool(toolSlug: string): ScoringNotice[] {
  return SCORING_NOTICES.filter(n => n.tools.includes(toolSlug)).sort((a, b) =>
    b.date.localeCompare(a.date)
  )
}

/** Canonical archive link, optionally anchored to one notice. */
export function methodologyHref(noticeId?: string): string {
  return noticeId
    ? `/platform/methodology#${noticeId}`
    : '/platform/methodology'
}
